/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { IconButton, InputAdornment, TextField } from '@mui/material';

import SimpleDatePicker from '../../../../Shared/Components/Common/SimpleDatePicker/SimpleDatePicker';
import { Icons } from '../../../../Shared/Constants/Icons';
import theme from '../../../../Shared/Themes/theme';
import { FieldNames } from '../../../Jobs/Models/AddEditOneTimeJob.Model';

interface IDateColumnFilterProps {
  column: {
    id: string;
    columnDef: { header: string };
    getFilterValue: () => unknown;
    setFilterValue: (value: unknown) => void;
  };
  table: {
    options: { localization: { filterByColumn?: string } };
  };
}


const DateColumnFilter = ({ column, table }: IDateColumnFilterProps): JSX.Element => {
  const { t } = useTranslation(['altTexts']);
  const filterValue = column.getFilterValue() as string | undefined;

  const placeholderText = table.options.localization.filterByColumn?.replace(
    '{column}',
    String(column.columnDef.header)
  );

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    column.setFilterValue(undefined);
  };

  return (
    <SimpleDatePicker
      isResponsive
      id={`${column.id}-filter`}
      placeholderText={placeholderText}
      defaultValue={filterValue ? moment(filterValue, FieldNames.dateFormat).toDate() : null}
      onChange={(date: string) => column.setFilterValue(date)}
      CustomComponent={
        <TextField
          variant="standard"
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: '1rem',
              border: `0.0625rem solid ${theme.palette.primary.dark}`,
              padding: '0 0.5rem',
              '&:hover:not(.Mui-disabled, .Mui-error)': {
                '&::before': { borderBottom: 'none' },
              },
              '&::before,&::after': { borderBottom: 'none' },
            },
            '& .MuiInputBase-input': {
              paddingLeft: '0.625rem',
              cursor: 'pointer',
            },
          }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                {filterValue ? (
                  <IconButton size="small" onClick={handleClear} aria-label={t('altTexts:clear')}>
                    <img
                      src={Icons.ClearIcon}
                      alt={t('altTexts:clear')}
                      style={{ width: '1rem', height: '1rem' }}
                    />
                  </IconButton>
                ) : (
                  <img
                    src={Icons.CalendarIcon}
                    alt={t('altTexts:calendar')}
                    style={{ width: '1.25rem', height: '1.25rem', filter: `brightness(0)` }}
                  />
                )}
              </InputAdornment>
            ),
          }}
        />
      }
    />
  );
};

export default DateColumnFilter;
